import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";
import Papa from "papaparse";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileSpreadsheet, Loader2, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FIELD_OPTIONS = [
  { value: "member_name", label: "Member Name" }, { value: "amount", label: "Amount" },
  { value: "category", label: "Category" }, { value: "date", label: "Date" },
  { value: "payment_method", label: "Payment Method" }, { value: "notes", label: "Notes" },
  { value: "_skip", label: "— Skip —" },
];

const AUTO_MAP: Record<string, string> = {
  name: "member_name", member: "member_name", "full name": "member_name", "member name": "member_name",
  full_name: "member_name", membername: "member_name",
  amount: "amount", donation: "amount", offering: "amount", tithe: "amount", total: "amount", sum: "amount",
  category: "category", type: "category", "income type": "category",
  date: "date", "transaction date": "date", "trans date": "date",
  payment: "payment_method", "payment method": "payment_method", method: "payment_method",
  notes: "notes", note: "notes", comment: "notes", description: "notes",
};

const STORAGE_KEY = "finance_csv_mapping";
function loadSavedMapping(): Record<string, string> { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; } }
function saveMappingConfig(mapping: Record<string, string>) { localStorage.setItem(STORAGE_KEY, JSON.stringify(mapping)); }

export default function CsvImport({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState<"upload" | "map" | "preview">("upload");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [fileName, setFileName] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { profile } = useChurch();
  const queryClient = useQueryClient();

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (result) => {
        if (result.data.length === 0) { toast.error("No data found"); return; }
        const hdrs = result.meta.fields || [];
        setHeaders(hdrs); setRows(result.data as Record<string, string>[]);
        const saved = loadSavedMapping();
        const autoMap: Record<string, string> = {};
        hdrs.forEach((h) => { const l = h.toLowerCase().trim(); if (saved[l]) autoMap[h] = saved[l]; else if (AUTO_MAP[l]) autoMap[h] = AUTO_MAP[l]; });
        setMapping(autoMap); setStep("map");
      },
      error: (err) => toast.error("Failed to parse: " + err.message),
    });
  };

  const updateMapping = (header: string, field: string) => setMapping((prev) => ({ ...prev, [header]: field }));
  const mappedFields = Object.values(mapping).filter((v) => v !== "_skip");
  const hasAmount = mappedFields.includes("amount");
  const hasCategory = mappedFields.includes("category");

  const handleConfirmMapping = () => {
    if (!hasAmount) { toast.error("Map the Amount column"); return; }
    const cfg: Record<string, string> = {};
    Object.entries(mapping).forEach(([h, f]) => { cfg[h.toLowerCase().trim()] = f; });
    saveMappingConfig(cfg); setStep("preview");
  };

  const getMappedValue = (row: Record<string, string>, field: string) => {
    const header = Object.entries(mapping).find(([, f]) => f === field)?.[0];
    return header ? row[header]?.trim() || "" : "";
  };

  const previewRows = rows.slice(0, 5).map((row) => ({
    member_name: getMappedValue(row, "member_name"),
    amount: getMappedValue(row, "amount"),
    category: getMappedValue(row, "category") || "Donations",
    date: getMappedValue(row, "date") || new Date().toISOString().split("T")[0],
  }));

  const importMutation = useMutation({
    mutationFn: async () => {
      const payload = rows.map((row) => {
        const amountStr = getMappedValue(row, "amount").replace(/[^0-9.-]/g, "");
        const amount = parseFloat(amountStr);
        if (isNaN(amount) || amount <= 0) return null;
        const rawDate = getMappedValue(row, "date");
        let date = new Date().toISOString().split("T")[0];
        if (rawDate) { const p = new Date(rawDate); if (!isNaN(p.getTime())) date = p.toISOString().split("T")[0]; }
        return {
          member_name: getMappedValue(row, "member_name") || null, amount, category: getMappedValue(row, "category") || "Donations",
          date, payment_method: getMappedValue(row, "payment_method") || null, notes: getMappedValue(row, "notes") || null,
          created_by: user?.id || null, church_id: profile?.id,
        };
      }).filter(Boolean) as any[];
      if (payload.length === 0) throw new Error("No valid rows");
      for (let i = 0; i < payload.length; i += 100) {
        const { error } = await supabase.from("finance_records").insert(payload.slice(i, i + 100));
        if (error) throw error;
      }
      return payload.length;
    },
    onSuccess: (count) => { queryClient.invalidateQueries({ queryKey: ["finance_records"] }); queryClient.invalidateQueries({ queryKey: ["dashboard_finance"] });  setStep("upload"); setRows([]); setHeaders([]); setMapping({}); setFileName(""); onDone(); },
    onError: (err: Error) => toast.error(err.message),
  });

  if (step === "upload") {
    return (
      <div className="stat-card">
        <div className="border-2 border-dashed border-border rounded-xl p-12 text-center cursor-pointer hover:border-primary/40 transition-colors" onClick={() => fileRef.current?.click()}>
          <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
          <p className="font-medium text-foreground">Click to upload CSV file</p>
          <p className="text-sm text-muted-foreground mt-1">Supports .csv files with any column format</p>
          <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" className="hidden" onChange={handleFile} />
        </div>
      </div>
    );
  }

  if (step === "map") {
    return (
      <div className="stat-card space-y-5">
        <div className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-primary" /><h3 className="font-heading font-semibold text-foreground">Map Columns</h3><Badge variant="secondary" className="ml-auto">{fileName}</Badge></div>
        <p className="text-sm text-muted-foreground">{headers.length} columns, {rows.length} rows. Map each column:</p>
        <div className="space-y-3">
          {headers.map((h) => (
            <div key={h} className="flex items-center gap-4">
              <span className="text-sm font-medium text-foreground w-40 truncate" title={h}>"{h}"</span>
              <span className="text-muted-foreground text-sm">→</span>
              <Select value={mapping[h] || "_skip"} onValueChange={(v) => updateMapping(h, v)}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_OPTIONS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
              {mapping[h] && mapping[h] !== "_skip" && <Check className="w-4 h-4 text-success" />}
            </div>
          ))}
        </div>
        {!hasAmount && <div className="flex items-center gap-2 text-destructive text-sm"><AlertTriangle className="w-4 h-4" />Amount column must be mapped</div>}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => { setStep("upload"); setRows([]); setHeaders([]); }}>Back</Button>
          <Button onClick={handleConfirmMapping} disabled={!hasAmount}>Preview Import</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="stat-card space-y-5">
      <div className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-primary" /><h3 className="font-heading font-semibold text-foreground">Preview Import</h3><Badge variant="secondary" className="ml-auto">{rows.length} records</Badge></div>
      <p className="text-sm text-muted-foreground">Showing first {Math.min(5, rows.length)} of {rows.length}:</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border"><th className="text-left px-3 py-2 text-xs text-muted-foreground uppercase">Member</th><th className="text-left px-3 py-2 text-xs text-muted-foreground uppercase">Amount</th><th className="text-left px-3 py-2 text-xs text-muted-foreground uppercase">Category</th><th className="text-left px-3 py-2 text-xs text-muted-foreground uppercase">Date</th></tr></thead>
          <tbody>{previewRows.map((r, i) => (<tr key={i} className="border-b border-border/50"><td className="px-3 py-2 text-muted-foreground">{r.member_name || "—"}</td><td className="px-3 py-2 font-medium">{r.amount}</td><td className="px-3 py-2"><Badge variant="secondary">{r.category}</Badge></td><td className="px-3 py-2 text-muted-foreground">{r.date}</td></tr>))}</tbody>
        </table>
      </div>
      {!hasCategory && <p className="text-xs text-muted-foreground">No category mapped — defaulting to "Donations"</p>}
      <div className="flex justify-end gap-3 pt-2">
        <Button variant="outline" onClick={() => setStep("map")}>Back</Button>
        <Button onClick={() => importMutation.mutate()} disabled={importMutation.isPending}>
          {importMutation.isPending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Importing...</> : <><Upload className="w-4 h-4 mr-1" /> Import {rows.length} Records</>}
        </Button>
      </div>
    </div>
  );
}
